import { useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, File, Trash2, Download, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useFileUpload } from "@/hooks/useFileUpload";

interface Document {
  id: string;
  filename: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  url: string;
  category: string | null;
  created_at: string;
}

interface DocumentsTabProps {
  jobId: string;
  documents: Document[];
  onDocumentsUpdate: () => void;
}

export const DocumentsTab = ({
  jobId,
  documents,
  onDocumentsUpdate,
}: DocumentsTabProps) => {
  const { user, orgId } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, uploading } = useFileUpload(orgId || '');

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !user || !orgId) return;

    try {
      for (const file of Array.from(files)) {
        const result = await uploadFile(file, {
          bucket: 'job-documents',
          folder: `${jobId}`,
        });

        if (!result) continue;

        // Get signed URL for secure access (valid for 1 year)
        const { data: signedUrlData } = await supabase.storage
          .from("job-documents")
          .createSignedUrl(result.path, 31536000);

        if (!signedUrlData) throw new Error("Failed to generate URL");

        // Save metadata to database
        const { error: dbError } = await supabase.from("documents").insert({
          job_id: jobId,
          org_id: orgId,
          filename: file.name,
          file_size: file.size,
          mime_type: file.type,
          storage_path: result.path,
          url: signedUrlData.signedUrl,
          uploaded_by: user.id,
        });

        if (dbError) throw dbError;

        // If it's a PDF with 'measurement' in the name, trigger measurement extraction
        if (file.type === 'application/pdf' && file.name.toLowerCase().includes('measurement')) {
          console.log('📄 Triggering PDF measurement extraction...');
          
          // Get the document ID we just created
          const { data: newDoc } = await supabase
            .from('documents')
            .select('id')
            .eq('storage_path', result.path)
            .single();

          if (newDoc) {
            // Trigger async PDF processing
            supabase.functions.invoke('process-measurement-pdf', {
              body: {
                document_id: newDoc.id,
                job_id: jobId,
                document_url: signedUrlData.signedUrl,
              },
            }).then((result) => {
              if (result.data?.success) {
                toast.success('📊 Measurements extracted from PDF!');
                onDocumentsUpdate();
              } else {
                console.error('PDF processing failed:', result.error);
              }
            });
          }
        }
      }

      toast.success(`${files.length} file(s) uploaded successfully`);
      onDocumentsUpdate();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      console.error("Error uploading files:", error);
      toast.error(error.message || "Failed to upload files");
    }
  };

  const handleDeleteDocument = async (docId: string, storagePath: string) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("job-documents")
        .remove([storagePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("documents")
        .delete()
        .eq("id", docId);

      if (dbError) throw dbError;

      toast.success("Document deleted");
      onDocumentsUpdate();
    } catch (error: any) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) return "📄";
    if (mimeType.includes("word") || mimeType.includes("document")) return "📝";
    if (mimeType.includes("sheet") || mimeType.includes("excel")) return "📊";
    if (mimeType.includes("image")) return "🖼️";
    return "📎";
  };

  return (
    <div className="container mx-auto px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Documents</h2>
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="gap-2"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          Upload Documents
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
        />
      </div>

      {documents.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <File className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No documents uploaded yet</p>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Upload Your First Document
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">
                      {getFileIcon(doc.mime_type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {doc.filename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={() => window.open(doc.url, "_blank")}
                  >
                    <Download className="w-3 h-3" />
                    Download
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      handleDeleteDocument(doc.id, doc.storage_path)
                    }
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>

                <div className="mt-2 text-xs text-muted-foreground">
                  {new Date(doc.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
